import React, { useState, useEffect, useRef } from "react";
import { createRoot } from "react-dom/client";
import Chart from "chart.js/auto";
import "./styles.css";

export default function CardiacRiskCalculator() {
    const [form, setForm] = useState({
        age: "",
        sex: "female",
        smoker: "no",
        nonHDL: "",
        systolic: "",
        region: "low"
    });

    const [result, setResult] = useState(null);    // 0–1
    const [category, setCategory] = useState(null);
    const chartRef = useRef(null);
    const chartInstance = useRef(null);

    // ================================================
    // ===  Fórmula DEMO (coherente pero ficticia)  ===
    // ================================================
    function calculateScoreDemo({ age, sex, smoker, nonHDL, systolic }) {
        const factorSexo = sex === "male" ? 1.1 : 1.0;
        const factorFumador = smoker === "yes" ? 1.3 : 1.0;

        // Fórmula inventada pero con sentido epidemiológico
        const raw =
            (age * 0.15) +
            (nonHDL * 0.05) +
            (systolic * 0.04) *
            factorSexo *
            factorFumador;

        // Normalizar entre 0–100 %
        const riesgo = Math.min(Math.round(raw), 100);

        return riesgo / 100; // devolver 0–1 para el gráfico
    }

    function classify(score) {
        const p = score * 100;

        if (p < 20) return "bajo";
        if (p < 26) return "moderado";
        return "alto";
    }

    function handleChange(e) {
        setForm({ ...form, [e.target.name]: e.target.value });
    }

    function onSubmit(e) {
        e.preventDefault();

        const numeric = {
            age: Number(form.age),
            sex: form.sex,
            smoker: form.smoker,
            nonHDL: Number(form.nonHDL),
            systolic: Number(form.systolic),
            region: form.region
        };

        const score = calculateScoreDemo(numeric);
        const cat = classify(score);

        setResult(score);
        setCategory(cat);
    }

    const handleReset = () => {
        setForm({
            age: "",
            sex: "female",
            smoker: "no",
            nonHDL: "",
            systolic: "",
            region: "low"
        });

        setResult(null);
        setCategory(null);

        // destruir gráfica si existe
        if (chartInstance.current) {
            chartInstance.current.destroy();
            chartInstance.current = null;
        }
    };



    // ======================================================
    // === Render animado del gráfico de pastel (Chart.js) ===
    // ======================================================
    useEffect(() => {
        if (result === null) return;

        const ctx = chartRef.current?.getContext("2d");
        if (!ctx) return;

        if (chartInstance.current) chartInstance.current.destroy();

        const scorePercent = Number((result * 100).toFixed(1));

        let color = "#2ecc71";
        if (category === "moderado") color = "#f1c40f";
        if (category === "alto") color = "#e74c3c";

        chartInstance.current = new Chart(ctx, {
            type: "pie",
            data: {
                labels: ["Riesgo %", "Resto"],
                datasets: [
                    {
                        data: [scorePercent, 100 - scorePercent],
                        backgroundColor: [color, "#e0e0e0"],
                        borderWidth: 0
                    }
                ]
            },
            options: {
                animation: {
                    animateRotate: true,
                    animateScale: true,
                    duration: 1200,
                    easing: "easeOutQuart"
                },
                plugins: {
                    legend: { display: false },
                    tooltip: {
                        enabled: true,
                        backgroundColor: "rgba(0,0,0,0.7)",
                        padding: 10,
                        titleFont: { size: 14 },
                        bodyFont: { size: 14 },
                        cornerRadius: 6
                    }
                }
            }
        });
    }, [result, category]);

    // ======================================================
    // ======================= UI ===========================
    // ======================================================

    return (
        <div className="cardiac-container">
            <h2 className="cardiac-title">Calculadora de Riesgo Cardiaco (Demo)</h2>
            <div className="cardiac-card">
                
                <form onSubmit={onSubmit} className="cardiac-form">
                    <div className="cardiac-grid">

                        <div>
                            <label>Edad</label>
                            <input
                                name="age"
                                type="number"
                                min="18"
                                max="90"
                                required
                                value={form.age}
                                onChange={handleChange}
                                className="cardiac-input"
                            />
                        </div>

                        <div>
                            <label>Sexo</label>
                            <select
                                name="sex"
                                value={form.sex}
                                onChange={handleChange}
                                className="cardiac-input-select"
                            >
                                <option value="female">Femenino</option>
                                <option value="male">Masculino</option>
                            </select>
                        </div>

                        <div>
                            <label>Fumador</label>
                            <select
                                name="smoker"
                                value={form.smoker}
                                onChange={handleChange}
                                className="cardiac-input-select"
                            >
                                <option value="no">No</option>
                                <option value="yes">Sí</option>
                            </select>
                        </div>

                        <div>
                            <label>Colesterol no-HDL (mg/dL)</label>
                            <input
                                name="nonHDL"
                                type="number"
                                min="20"
                                max="400"
                                required
                                value={form.nonHDL}
                                onChange={handleChange}
                                className="cardiac-input"
                            />
                        </div>

                        <div>
                            <label>Presión sistólica (mmHg)</label>
                            <input
                                name="systolic"
                                type="number"
                                min="80"
                                max="250"
                                required
                                value={form.systolic}
                                onChange={handleChange}
                                className="cardiac-input"
                            />
                        </div>

                        <div>
                            <label>Región</label>
                            <select
                                name="region"
                                value={form.region}
                                onChange={handleChange}
                                className="cardiac-input-select"
                            >
                                <option value="low">Bajo riesgo</option>
                                <option value="moderate">Moderado</option>
                                <option value="high">Alto</option>
                            </select>
                        </div>

                    </div>

                    <button className="cardiac-button">Calcular</button>
                    <button className="cardiac-button" type="button" onClick={handleReset}>Reset</button>

                </form>

                {result === null && (
                <div className="cardiac-result-img">

                </div>
                )}

                {result !== null && (
                    <div className="cardiac-result-card">
                        <h3 className="cardiac-result-title">Resultado</h3>

                        <div className="cardiac-chart-wrapper">
                            <canvas ref={chartRef} width="200" height="200"></canvas>
                        </div>

                        <p className="cardiac-percent">
                            {Number((result * 100).toFixed(1))}%
                        </p>

                        <p className="cardiac-category">
                            Riesgo: <strong>{category.toUpperCase()}</strong>
                        </p>
                        {category === "bajo" && (
                            <p className="cardiac-contenido">
                                Según los valores ingresados, su riesgo cardiovascular estimado es **bajo**.  
                                Mantenga sus hábitos saludables, controle periódicamente presión y lípidos,  
                                y continúe con actividad física regular. No se requieren intervenciones adicionales  
                                en este momento, salvo seguimiento rutinario.
                            </p>
                        )} 
                        {category === "moderado" && (
                            <p className="cardiac-contenido">
                                Su riesgo cardiovascular estimado es **moderado**.  
                                Es recomendable optimizar su estilo de vida: mejorar la alimentación, aumentar la actividad física  
                                y evitar el tabaco. Considere realizar un control clínico para evaluar presión arterial, perfil lipídico  
                                y posibles intervenciones preventivas adicionales.
                            </p>
                        )}  
                        {category === "alto" && (
                            <p className="cardiac-contenido">
                                Su riesgo cardiovascular estimado es **alto**.  
                                Es importante realizar una valoración médica completa lo antes posible.  
                                El control estricto de la presión arterial, los lípidos y los factores de riesgo asociados  
                                puede reducir de forma significativa la probabilidad de eventos cardiovasculares.  
                                No retrase la consulta con un profesional de salud.
                            </p>
                        )}
                    
                    </div>
                )}
            </div>
        </div>
    );
}

// --- MONTAJE EN WORDPRESS ---
document.addEventListener("DOMContentLoaded", () => {
    const rootDiv = document.getElementById("crc-root");
    if (rootDiv) {
        const root = createRoot(rootDiv);
        root.render(<CardiacRiskCalculator />);
    }
});

